import { FeaturedIcons, useTheme } from '@agrotoken-tech/ui';
import { STATUS_ICON } from '@bcba/frontend-utils';
import { useTranslation } from '@bcba/react-hooks';
import { VALIDATION_STATUSES } from '@bcba/types';
import { useQuery } from '@tanstack/react-query';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useMemo } from 'react';
import { getEquityInfo } from '../../../services/company';
import { AffidavitsSection } from '../AffidavitsSection';
import { ContactInfoSection } from '../ContactInfoSection';
import { useDigitalDossierSectionContext } from '../context/DigitalDossierSectionContext';
import { DigitalDossierSection } from '../DigitalDossierSection';
import EquityInfoSection from '../EquityInfoSection';
import { TabData } from '../shared/DigitalDossierLayout';

const useDigitalDossier = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  const { digitalDossierStatus } = useDigitalDossierSectionContext();

  const {
    explorerDigitalDossierEnableContactInfo,
    explorerDigitalDossierEnableEstablishmentsInfo,
    explorerDigitalDossierEnablePatrimonialInfo,
  } = useFlags();

  const {
    data: equityInfo,
    isPending,
    refetch,
    isError,
  } = useQuery({
    queryKey: ['equity-info'],
    queryFn: getEquityInfo,
  });
  const showEquityInfoStatusIcon =
    equityInfo?.status !== VALIDATION_STATUSES.INCOMPLETE &&
    equityInfo?.status !== undefined;

  const tabsData = useMemo(() => {
    const tabs: TabData[] = [
      {
        title: t('common.digitalDossier'),
        content: <DigitalDossierSection />,
      },
    ];

    if (explorerDigitalDossierEnableContactInfo) {
      tabs.push({
        title: t('common.contactInformation'),
        content: <ContactInfoSection />,
      });
    }

    if (explorerDigitalDossierEnableEstablishmentsInfo) {
      tabs.push({
        title: t('common.affidavits'),
        content: <AffidavitsSection />,
      });
    }

    if (explorerDigitalDossierEnablePatrimonialInfo) {
      tabs.push({
        title: t('common.equityInformation'),
        icon: (
          <>
            {showEquityInfoStatusIcon && (
              <FeaturedIcons
                icon={
                  STATUS_ICON[
                    equityInfo?.status ?? VALIDATION_STATUSES.INCOMPLETE
                  ]
                }
                variant={'primary'}
                size="sm"
              />
            )}
          </>
        ),
        content: (
          <EquityInfoSection
            data={equityInfo}
            isPending={isPending}
            refetch={refetch}
            isError={isError}
          />
        ),
      });
    }

    return tabs;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    t,
    explorerDigitalDossierEnableContactInfo,
    explorerDigitalDossierEnableEstablishmentsInfo,
    explorerDigitalDossierEnablePatrimonialInfo,
    equityInfo,
  ]);

  return { theme, tabsData, digitalDossierStatus };
};

export default useDigitalDossier;
